import {
  type CompanyListItem,
  createPlanBody,
  type PlanDto,
  type TargetedPlanDto,
  type UserListItem,
  updatePlanBody,
} from '@planuze/admin-contracts';
import { useMemo, useState } from 'react';
import { usePlansList } from './use-plans.js';

type PlanCaps = Record<string, boolean | number>;
type PlanInterval = 'month' | 'year';
type PlanTargetType = 'organization' | 'user';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface UsePlanFormOptions {
  /** Ausente = criar; presente = editar metadata mutável do catálogo (ADR-0888). */
  readonly plan?: PlanDto | TargetedPlanDto | undefined;
  /** Modo DIRECIONADO (ADR-0435) durante a criação. Na edição o DTO discrimina o modo. */
  readonly targeted?: boolean | undefined;
}

function isTargetedPlan(plan: PlanDto | TargetedPlanDto): plan is TargetedPlanDto {
  return 'orgId' in plan;
}

function dateInput(value: string | null | undefined): string {
  return value?.slice(0, 10) ?? '';
}

/* Estado + derivações do formulário de plano (ADR-0704), extraído para reuso entre a página
   de criação (pública e negociada) e o modal de edição. O state é inicializado via lazy
   initializer a partir de `plan` — cada montagem parte do valor certo, sem `useEffect` de
   reset. Deriva `isOrgPlan`, `effectiveCapabilities`, os campos de alvo/assento e a validação
   Zod; NÃO dispara mutation (o call-site é dono do side-effect + navegação/toast). */
export function usePlanForm({ plan, targeted = false }: UsePlanFormOptions) {
  const isEdit = plan !== undefined;
  const targetedPlan = plan !== undefined && isTargetedPlan(plan) ? plan : undefined;
  const isTargetedEdit = targetedPlan !== undefined;
  const isTargeted = isTargetedEdit || (targeted && !isEdit);
  const initialValidFrom =
    targetedPlan === undefined ? todayIso() : dateInput(targetedPlan.validFrom);
  const initialValidUntil = targetedPlan === undefined ? '' : dateInput(targetedPlan.validUntil);
  const initialReason = targetedPlan?.reason ?? '';
  const initialDisplayName = plan?.displayName ?? '';

  const [tier, setTier] = useState(plan?.tier ?? '');
  const [planKey, setPlanKey] = useState(plan?.lookupKey ?? '');
  const [displayName, setDisplayName] = useState(plan?.displayName ?? '');
  const [priceCents, setPriceCents] = useState(plan?.priceUsdCents ?? 0);
  const [interval, setIntervalValue] = useState<PlanInterval>(
    (plan?.interval as PlanInterval | undefined) ?? 'month',
  );
  const [rank, setRank] = useState(plan?.rank ?? 0);
  const [capabilities, setCapabilities] = useState<PlanCaps>({ ...(plan?.capabilities ?? {}) });
  /* Model B (ADR-0653): assentos inclusos na base + preço do assento excedente (só plano de org). */
  const [includedSeats, setIncludedSeats] = useState(plan?.includedSeats ?? 0);
  const [extraSeatPriceCents, setExtraSeatPriceCents] = useState(plan?.extraSeatPriceCents ?? 0);
  /* Visibilidade nas listagens comuns do app (ADR-0508). Só em plano público (não direcionado). */
  const [isPublic, setIsPublic] = useState(
    plan !== undefined && !isTargetedPlan(plan) ? plan.isPublic : !isTargeted,
  );
  /* Campos de direcionamento (ADR-0435). Alvo só é selecionável na criação; na edição
     `targetType` serve para derivar assentos e apresentar o alvo imutável. */
  const [targetType, setTargetType] = useState<PlanTargetType>(
    targetedPlan?.userId !== null && targetedPlan?.userId !== undefined ? 'user' : 'organization',
  );
  const [org, setOrg] = useState<CompanyListItem | null>(null);
  const [user, setUser] = useState<UserListItem | null>(null);
  const [validFrom, setValidFrom] = useState(initialValidFrom);
  const [validUntil, setValidUntil] = useState(initialValidUntil);
  const [reason, setReason] = useState(initialReason);
  const reasonChanged = reason !== initialReason;
  const reasonValid = !reasonChanged || (reason.trim().length >= 10 && reason.trim().length <= 500);
  /* Planos-fonte cujas caps de packages/templates este plano herda (ADR-0595) — só direcionado. */
  const [inheritedPlanKeys, setInheritedPlanKeys] = useState<string[]>(
    targetedPlan === undefined ? [] : [...targetedPlan.inheritedPlanKeys],
  );

  const plansList = usePlansList();
  /* Fontes de herança = planos públicos (páginas carregadas), deduplicados por tier (month/year
     compartilham caps). A lista é paginada (ADR-0713); as fontes usam o que já foi carregado. */
  const sourcePlans = useMemo(() => {
    const loaded = plansList.data?.pages.flatMap((page) => page.items) ?? [];
    return Array.from(new Map(loaded.map((p) => [p.tier, p])).values());
  }, [plansList.data]);

  /* Plano de ORG (Model B, ADR-0653): cobrança por assento só faz sentido em plano direcionado a
     ORGANIZAÇÃO; `isPublic=false` num plano público não transforma seu alvo. */
  const isOrgPlan = isTargeted && targetType === 'organization';
  /* `capabilities.seats` (teto do ADR-0646) foi SUPERSEDIDO pelos campos de 1ª classe do Model B
     (includedSeats/extraSeatPriceCents, ADR-0653) → sempre removido do payload; valor stale de seed
     antigo é LIMPO ao salvar. */
  const effectiveCapabilities = useMemo<PlanCaps>(
    () => Object.fromEntries(Object.entries(capabilities).filter(([k]) => k !== 'seats')),
    [capabilities],
  );
  /* Fora de plano de org, inclusos/preço-extra são 0 (não se aplicam). */
  const seatFields = isOrgPlan ? { includedSeats, extraSeatPriceCents } : {};

  /* Campos de alvo/validade (ADR-0435): só no modo direcionado. `orgId` XOR `userId`. */
  const targetFields = isTargeted
    ? {
        ...(targetType === 'organization' && org !== null ? { orgId: org.id } : {}),
        ...(targetType === 'user' && user !== null ? { userId: user.id } : {}),
        ...(validFrom.length > 0
          ? { validFrom: new Date(`${validFrom}T00:00:00.000Z`).toISOString() }
          : {}),
        ...(validUntil.length > 0
          ? { validUntil: new Date(`${validUntil}T00:00:00.000Z`).toISOString() }
          : {}),
        reason,
      }
    : {};
  const createInput = {
    tier,
    planKey,
    displayName,
    priceCents,
    currency: 'usd',
    interval,
    rank,
    capabilities: effectiveCapabilities,
    inheritedPlanKeys,
    /* Plano direcionado nunca é público (ADR-0508/0697): o switch "Público no app" só aparece
       fora do modo direcionado, mas o state inicia em true — força false aqui. */
    isPublic: isTargeted ? false : isPublic,
    ...seatFields,
    ...targetFields,
  };
  const updateInput = {
    expectedUpdatedAt: plan?.updatedAt ?? '',
    /* A API projeta rows antigas sem nome como `planKey` para exibição. Reenviar o fallback
       intacto converteria uma edição de rank/capability em rename real no Stripe. */
    ...(displayName !== initialDisplayName ? { displayName } : {}),
    tier,
    rank,
    capabilities: effectiveCapabilities,
    ...(isTargeted
      ? {
          inheritedPlanKeys,
          /* O input `date` não representa horário. Datas intactas são omitidas para preservar o
             timestamp original; ao alterar, a convenção vigente continua meia-noite UTC. */
          ...(validFrom !== initialValidFrom && {
            validFrom:
              validFrom.length > 0 ? new Date(`${validFrom}T00:00:00.000Z`).toISOString() : null,
          }),
          ...(validUntil !== initialValidUntil && {
            validUntil:
              validUntil.length > 0 ? new Date(`${validUntil}T00:00:00.000Z`).toISOString() : null,
          }),
          /* Rows legadas sem motivo continuam editáveis enquanto o campo fica intacto. Qualquer
             alteração — inclusive limpar — é enviada e validada explicitamente pelo contrato. */
          ...(reasonChanged ? { reason } : {}),
        }
      : { isPublic }),
    /* O preço do assento extra é um Stripe Price imutável e nunca entra na edição. */
    ...(isOrgPlan ? { includedSeats } : {}),
  };
  /* No modo direcionado, um alvo (org XOR usuário) é OBRIGATÓRIO. Sem ele, `targetFields` omite
     orgId/userId e o contrato trata como plano PÚBLICO — criando um público disfarçado de
     negociado (vaza o preço). O gate exige o alvo antes de habilitar o submit (ADR-0697). */
  const hasTarget =
    isTargetedEdit || (targetType === 'organization' ? org !== null : user !== null);
  const validityWindowValid =
    !isTargeted ||
    validFrom.length === 0 ||
    validUntil.length === 0 ||
    Date.parse(`${validUntil}T00:00:00.000Z`) > Date.parse(`${validFrom}T00:00:00.000Z`);
  const valid = isEdit
    ? updatePlanBody.safeParse(updateInput).success && validityWindowValid
    : createPlanBody.safeParse(createInput).success &&
      (!isTargeted || hasTarget) &&
      validityWindowValid;

  return {
    isEdit,
    isTargeted,
    isOrgPlan,
    tier,
    setTier,
    planKey,
    setPlanKey,
    displayName,
    setDisplayName,
    priceCents,
    setPriceCents,
    interval,
    setInterval: setIntervalValue,
    rank,
    setRank,
    capabilities,
    setCapabilities,
    includedSeats,
    setIncludedSeats,
    extraSeatPriceCents,
    setExtraSeatPriceCents,
    isPublic,
    setIsPublic,
    targetType,
    setTargetType,
    org,
    setOrg,
    user,
    setUser,
    validFrom,
    setValidFrom,
    validUntil,
    setValidUntil,
    reason,
    setReason,
    reasonChanged,
    reasonValid,
    inheritedPlanKeys,
    setInheritedPlanKeys,
    sourcePlans,
    effectiveCapabilities,
    createInput,
    updateInput,
    hasTarget,
    valid,
  };
}

export type PlanForm = ReturnType<typeof usePlanForm>;
